import { createContext, ReactNode, useContext, useEffect } from 'react'
import { AccountTree } from '@mui/icons-material'
import useMetricsSearchParams from '@/hooks/useMetricsSearchParams'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import { Workflow } from '@/types/workflows'
import useRegisterRelevantID from '@/components/relevant-ids/useRegisterRelevantID'
import { useWorkflowsContext } from '@/components/workflows-provider/WorkflowsProvider'

type SelectedWorkflowContextValue = {
  workflows: Workflow[]
  selectedWorkflow: Workflow
  setSelectedWorkflow: (newWorkflowId: string) => void
}

const SelectedWorkflowContext = createContext<SelectedWorkflowContextValue>(
  {} as SelectedWorkflowContextValue,
)

export const useSelectedWorkflowContext = () =>
  useContext(SelectedWorkflowContext)

type SelectedWorkflowProviderProps = {
  children: ReactNode
  defaultWorkflowId?: string
}

export default function SelectedWorkflowProvider({
  children,
}: SelectedWorkflowProviderProps) {
  const { workflows, productionDataWorkflow } = useWorkflowsContext()
  const { resetAllFilters } = useMetricsSearchParams()

  const { value: selectedWorkflowId, toggleValue: setSelectedWorkflowId } =
    useStatefulSearchParam({
      param: 'workflow',
      defaultValue: productionDataWorkflow?.id,
      fieldsToClearOnChange: ['workflow_state'],
    })

  const selectedWorkflow = workflows.find(({ id }) => id === selectedWorkflowId)

  useRegisterRelevantID(
    selectedWorkflow
      ? {
          group: 'Top Level',
          type: 'Workflow',
          label: selectedWorkflow.name,
          id: selectedWorkflow.id,
          icon: AccountTree,
          color: 'teal',
        }
      : null,
  )

  // If the workflow is missing for some reason, set to default workflow.
  useEffect(() => {
    if (productionDataWorkflow && !selectedWorkflow) {
      setSelectedWorkflowId(productionDataWorkflow.id)
    }
  }, [
    productionDataWorkflow,
    selectedWorkflow,
    setSelectedWorkflowId,
    workflows,
  ])

  useEffect(() => {
    if (selectedWorkflow?.project_model_id) {
      resetAllFilters()
    }
  }, [resetAllFilters, selectedWorkflow?.project_model_id])

  return (
    <SelectedWorkflowContext.Provider
      value={{
        workflows,
        selectedWorkflow: selectedWorkflow as Workflow,
        setSelectedWorkflow: setSelectedWorkflowId,
      }}
    >
      {workflows.length > 0 && children}
    </SelectedWorkflowContext.Provider>
  )
}
